import moment from 'moment'

// Shared vocabulary for the tasks board, list and drawer. Task documents are
// loosely typed on the server (priority/status are free strings, and older
// tasks stored priority as a number), so everything normalises through here.

export const STATUSES = ['New', 'In Progress', 'Completed']

export const STATUS_META = {
  New: { label: 'New', colorKey: 'info' },
  'In Progress': { label: 'In progress', colorKey: 'warning' },
  Completed: { label: 'Completed', colorKey: 'success' },
}

export const normaliseStatus = (status) => {
  const value = String(status ?? '').toLowerCase()
  if (value === 'completed' || value === 'complete') return 'Completed'
  if (value === 'in progress') return 'In Progress'
  return 'New'
}

export const PRIORITIES = ['Urgent', 'High', 'Medium', 'Low']

export const PRIORITY_META = {
  Urgent: { colorKey: 'anRed', rank: 0 },
  High: { colorKey: 'anOrange', rank: 1 },
  Medium: { colorKey: 'anMainBlue', rank: 2 },
  Low: { colorKey: 'text.disabled', rank: 3 },
}

// AddNewTask used to save 1–4 instead of the label
const NUMERIC_PRIORITIES = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Urgent' }

export const normalisePriority = (priority) =>
  NUMERIC_PRIORITIES[priority] ??
  (PRIORITIES.includes(priority) ? priority : 'Medium')

const TEAM_COLOR_KEYS = {
  'Commercial Training': 'primary',
  Supervisor: 'anPurple',
  Manager: 'anPurple',
  Personnel: 'anGreen',
  Governance: 'anYellow',
  'Event Planning': 'anRed',
  'Make Ready': 'anBlue',
}

export const getTeamColorKey = (team) => TEAM_COLOR_KEYS[team] ?? 'anMainBlue'

// Resolve a palette key ('anRed', 'text.disabled', 'info') to a hex value
export const resolveColor = (theme, key) => {
  const [group, shade] = key.split('.')
  const entry = theme.palette[group]
  if (!entry) return key
  if (shade) return entry[shade] ?? entry.main
  return entry.main ?? entry
}

export const normaliseTask = (task) => ({
  ...task,
  taskStatus: normaliseStatus(task.taskStatus),
  taskPriority: normalisePriority(task.taskPriority),
})

// Where a task sits relative to today. Completed tasks never count as
// overdue — they show the date they were due instead.
export const getDueMeta = (task) => {
  if (!task.dueDate) return { bucket: 'none', label: 'No due date', color: 'default' }
  const today = moment().startOf('day')
  const due = moment(task.dueDate).startOf('day')
  const diff = due.diff(today, 'days')
  if (task.taskStatus === 'Completed')
    return { bucket: 'done', label: due.format('D MMM'), color: 'default' }
  if (diff < 0)
    return {
      bucket: 'overdue',
      label: diff === -1 ? 'Yesterday' : `${-diff} days overdue`,
      color: 'error',
      daysOverdue: -diff,
    }
  if (diff === 0) return { bucket: 'today', label: 'Today', color: 'warning' }
  if (diff === 1) return { bucket: 'week', label: 'Tomorrow', color: 'default' }
  if (diff < 7)
    return { bucket: 'week', label: due.format('ddd D MMM'), color: 'default' }
  return { bucket: 'later', label: due.format('D MMM'), color: 'default' }
}

export const BUCKET_META = {
  overdue: { label: 'Overdue', colorKey: 'anRed' },
  today: { label: 'Due today', colorKey: 'anOrange' },
  week: { label: 'This week', colorKey: 'text.primary' },
  later: { label: 'Later', colorKey: 'text.primary' },
  none: { label: 'No due date', colorKey: 'text.secondary' },
  done: { label: 'Completed', colorKey: 'anGreen' },
}

export const BUCKET_ORDER = ['overdue', 'today', 'week', 'later', 'none', 'done']

const idOf = (value) => String(value?._id ?? value ?? '')

export const isAssignedToMe = (task, userId) =>
  !!task.assignedToUser && idOf(task.assignedToUser) === String(userId)

export const getAssigneeName = (task) =>
  task.assignedToUser
    ? `${task.assignedToUser.firstName ?? ''} ${
        task.assignedToUser.lastName ?? ''
      }`.trim()
    : task.assignedToTeam ?? ''

// The record a task was raised against, when the list endpoint populated it
export const getLinkedRecord = (task) => {
  const links = task.links ?? {}
  if (links.event)
    return {
      type: 'Event',
      label: links.event.title,
      url: `/events/${idOf(links.event)}`,
    }
  if (links.incident)
    return {
      type: 'Incident',
      label: links.incident.incidentNumber,
      url: `/incidents/${idOf(links.incident)}`,
    }
  if (links.vehicle)
    return {
      type: 'Vehicle',
      label: links.vehicle.callSign,
      url: `/fleet/${idOf(links.vehicle)}`,
    }
  if (links.defect)
    return {
      type: 'Defect',
      label: links.defect.summary,
      url: `/defects/${idOf(links.defect)}`,
    }
  if (links.safetyAlert)
    return {
      type: 'Safety alert',
      label: links.safetyAlert.title,
      url: `/safety-alerts/${idOf(links.safetyAlert)}`,
    }
  return null
}

export const sortTasks = (tasks, sortBy) => {
  const FAR_FUTURE = 8640000000000000
  const dueTime = (task) =>
    task.dueDate ? new Date(task.dueDate).getTime() : FAR_FUTURE
  const sorters = {
    dueDate: (a, b) => dueTime(a) - dueTime(b),
    priority: (a, b) =>
      PRIORITY_META[a.taskPriority].rank - PRIORITY_META[b.taskPriority].rank ||
      dueTime(a) - dueTime(b),
    newest: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  }
  return [...tasks].sort(sorters[sortBy] ?? sorters.dueDate)
}
